import React, { useState, useEffect } from 'react';
import styles from './Question.module.css';
import type { Question as QuestionType, Answer } from '@/types';

interface QuestionProps {
  question: QuestionType;
  previousAnswer?: Answer;
  onAnswer: (answer: Answer) => void;
  onNext: () => void;
  onPrevious: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export const Question: React.FC<QuestionProps> = ({
  question,
  previousAnswer,
  onAnswer,
  onNext,
  onPrevious,
  isFirst,
  isLast,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(previousAnswer?.values || []);
  const [error, setError] = useState<string>('');

  // Reset state when question changes
  useEffect(() => {
    setSelectedOptions(previousAnswer?.values || []);
    setError('');
  }, [question.id, previousAnswer]);

  const handleOptionChange = (optionValue: string) => {
    if (question.isMultiple) {
      // Multiple choice - toggle selection
      setSelectedOptions(prev => {
        if (prev.includes(optionValue)) {
          return prev.filter(v => v !== optionValue);
        } else {
          return [...prev, optionValue];
        }
      });
    } else {
      // Single choice - replace selection
      setSelectedOptions([optionValue]);
    }
    setError('');
  };

  const handleNext = () => {
    if (selectedOptions.length === 0) {
      setError('Veuillez sélectionner au moins une option');
      return;
    }

    // Build answer object
    const selectedOptionData = question.options.filter(opt => selectedOptions.includes(opt.value));

    // Calculate total points from selected options
    const totalPoints = selectedOptionData.reduce((sum, opt) => sum + opt.points, 0);

    // Only add products from options that DON'T have exclusions
    // Options with exclusions are only for excluding/replacing products
    const productsToAdd = selectedOptionData
      .filter(opt => !opt.exclusions || opt.exclusions.length === 0)
      .flatMap(opt => opt.products || []);

    const answer: Answer = {
      questionId: question.id,
      values: selectedOptions,
      products: productsToAdd,  // Only products from non-exclusion options
      exclusions: selectedOptionData.flatMap(opt => opt.exclusions || []),
      alternatives: selectedOptionData.reduce(
        (acc, opt) => ({
          ...acc,
          ...(opt.alternatives || {}),
        }),
        {}
      ),
      timestamp: new Date().toISOString(),
      points: totalPoints, // Sum of points from each selected option
    };

    onAnswer(answer);
    onNext();
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          {question.emoji && <span className={styles.emoji}>{question.emoji}</span>}
          <h2 className={styles.title}>{question.title}</h2>
          {question.description && <p className={styles.description}>{question.description}</p>}
        </div>

        {error && (
          <div className={styles.error} role="alert">
            <span className={styles.errorIcon}>⚠️</span>
            {error}
          </div>
        )}

        <div className={styles.optionsContainer}>
          {question.options.map(option => (
            <label
              key={option.id}
              className={`${styles.option} ${
                selectedOptions.includes(option.value) ? styles.selected : ''
              }`}
            >
              <input
                type={question.isMultiple ? 'checkbox' : 'radio'}
                name={question.id}
                value={option.value}
                checked={selectedOptions.includes(option.value)}
                onChange={() => handleOptionChange(option.value)}
                className={question.isMultiple ? styles.checkboxInput : styles.radioInput}
              />
              <span className={styles.optionLabel}>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className={styles.navigation}>
        <button
          type="button"
          onClick={onPrevious}
          disabled={isFirst}
          className={`btn btn-secondary ${styles.button}`}
        >
          ← Retour
        </button>

        <button
          type="button"
          onClick={handleNext}
          className={`btn btn-primary ${styles.button} ${styles.buttonPrimary}`}
        >
          {isLast ? 'Terminer' : 'Continuer'} →
        </button>
      </div>
    </div>
  );
};
